import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import ReactQuill,{Quill} from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'highlight.js/styles/github-dark.css';
import ImageResize from 'quill-image-resize-module-react';

// Custom code block format

if (typeof window !== 'undefined' && typeof Quill !== 'undefined' && !window.Quill) {
  window.Quill = Quill;
}

Quill.register('modules/imageResize', ImageResize);
const Image = ReactQuill.Quill.import('formats/image');
class CustomImage extends Image {
  static create(value) {
    let node = super.create(value);
    
    if (typeof value === 'object') {
      // Handle object format with attributes
      const { src, width, height, style } = value;
      node.setAttribute('src', src);
      
      if (width) node.setAttribute('width', width);
      if (height) node.setAttribute('height', height);
      
      node.style.maxWidth = '100%';
      node.style.height = 'auto';
      node.style.objectFit = 'contain';

      if (style) {
        Object.assign(node.style, style);
      }
    } else if (typeof value === 'string') {
      node.setAttribute('src', value);
      node.style.maxWidth = '100%';
      node.style.height = 'auto';
      node.style.objectFit = 'contain';
    }
    
    return node;
  }
 static value(domNode) {
    return {
      src: domNode.getAttribute('src'),
      width: domNode.getAttribute('width'),
      height: domNode.getAttribute('height'),
      style: {
        maxWidth: domNode.style.maxWidth,
        height: domNode.style.height,
        objectFit: domNode.style.objectFit,
        cursor: domNode.style.cursor
      }
    };
  }
}
Quill.register(CustomImage, true);

const RichTextEditor = forwardRef(({ value, onChange, placeholder }, ref) => {
  const quillRef = React.useRef(null);
  const [isMounted, setIsMounted] = useState(false);

  // Initialize editor content
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);
  useEffect(() => {
    if (isMounted && quillRef.current && value !== undefined) {
      const editor = quillRef.current.getEditor();
      const currentHtml = editor.root.innerHTML;
      if (currentHtml !== value) {
        editor.clipboard.dangerouslyPasteHTML(value || '');

        setTimeout(() => {
          const images = editor.root.querySelectorAll('img');
          images.forEach(img => {
            img.style.maxWidth = '100%';
            img.style.height = 'auto';
            img.style.objectFit = 'contain';
          });
        }, 100);
      }
    }
  }, [value, isMounted]);
  const handleChange = (content, delta, source, editor) => {
    if (source === 'user' && onChange) {
      onChange(editor.getHTML());
    }
  };
  useImperativeHandle(ref, () => ({
    getEditor: () => quillRef.current?.getEditor(),
    getHTML: () => quillRef.current?.getEditor()?.root.innerHTML,
    setContent: (html) => {
      if (quillRef.current) {
        quillRef.current.getEditor().clipboard.dangerouslyPasteHTML(html || '');
      }
    }
  }));

 const modules = {
  toolbar: {
    container: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['image'],
      ['clean'],
    ],    
  },
  clipboard: {
    matchVisual: false,
  },
  
   imageResize: { 
     parchment: Quill.import('parchment'),
    modules: ['Resize', 'DisplaySize', 'Toolbar'] 
  },
};

  const formats = [
    'bold', 'italic', 'underline', 'strike',
    'blockquote', 'code-block',
    'list', 'bullet','indent',
    'color','background',
    'header','align',
     'image'
  ];

  if (!isMounted) {
    return <div style={{ height: '200px', border: '1px solid #ccc' }} />;
  }

  return (
    <div className="rich-text-editor" style={{ width: '100%' }} >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        defaultValue={value || ''}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        style={{ width: '100%'}}
      />
       <style>
        {`
           /* Make the toolbar sticky */
           .rich-text-editor .ql-toolbar {
             position: sticky !important;
             font-weight: bold !important;
             top: 0;
             background: white;
             z-index: 1000;
             border-bottom: 1px solid #ccc;
           }

           /* Ensure the editor respects the sticky toolbar */
           .rich-text-editor .ql-container {
             height: auto;
           }

          /* Force image styling */
          .ql-editor img {
             max-width: 100% !important;
             height: auto !important;
             object-fit: contain !important;
             display: block !important;
           }
             .rich-text-editor .ql-editor strong {
              font-weight: bold !important;
            }
              .rich-text-editor .ql-picker.ql-expanded .ql-picker-options {
              max-height: 110px;            
              overflow-y: auto;              
            }
 

         `}
       </style>

    </div>

  );
});
 
export default RichTextEditor;